import { CheckCircle2, Copy, Link2, Plus, Shield, Trash2, UserPlus } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { api, formatCount, formatRelative } from "../api";
import { useAuth } from "../auth";
import { BoundaryCallout, Button, CopyButton, EmptyState, ErrorState, LoadingState, Modal } from "../components";
import { groupCountsLine, sharingCountsLine } from "./peopleCounts";
import { SharingStage, type SharingSummary } from "./SharingStage";
import "../styles/administration.css";

type Team = { id: string; name: string; description: string | null; color: string; memberCount: number; loopCount: number };
type Member = { id: string; email: string; displayName: string; role: string; title: string | null; joinedAt: string; teams: Array<{ id: string; name: string }> };
type Invitation = { id: string; email: string | null; role: string; team_id: string | null; team_name: string | null; expires_at: string; created_at: string; accepted_at: string | null; revoked_at: string | null };
type LoadState = "loading" | "ready" | "error";
type ApiRequest = <T>(path: string, options?: RequestInit, organizationId?: string | null) => Promise<T>;

export type PeopleLedgerContext = {
  organizationId: string;
  canManage: boolean;
  isPersonal: boolean;
};

export type PeopleLedgerData = {
  members: Member[];
  teams: Team[];
  invitations: Invitation[];
};

export async function requestPeopleLedger(
  context: PeopleLedgerContext,
  request: ApiRequest,
  isCurrent: () => boolean,
  onSuccess: (data: PeopleLedgerData) => void,
  onError: (error: unknown) => void,
) {
  try {
    const groupRequest = request<{ members: Member[]; teams: Team[] }>("/api/app/group", {}, context.organizationId);
    const invitationsRequest = context.canManage && !context.isPersonal
      ? request<{ invitations: Invitation[] }>("/api/app/invitations", {}, context.organizationId)
      : Promise.resolve({ invitations: [] as Invitation[] });
    const [group, invitationData] = await Promise.all([groupRequest, invitationsRequest]);
    if (!isCurrent()) return;
    onSuccess({ members: group.members, teams: group.teams, invitations: invitationData.invitations });
  } catch (caught) {
    if (!isCurrent()) return;
    onError(caught);
  }
}

function memberInitials(member: Member) {
  const source = member.displayName.trim() || member.email;
  return source.split(/\s+|@/u).filter(Boolean).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}

function invitationState(invitation: Invitation) {
  if (invitation.accepted_at) return "accepted";
  if (invitation.revoked_at) return "revoked";
  if (new Date(invitation.expires_at) < new Date()) return "expired";
  return "open";
}

export function PeoplePage() {
  const { activeOrganization } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [sharingSummary, setSharingSummary] = useState<SharingSummary | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const organizationId = activeOrganization?.id ?? "";
  const organizationRole = activeOrganization?.role ?? "";
  const organizationKind = activeOrganization?.kind ?? "personal";
  const isPersonal = activeOrganization?.kind === "personal";
  const canManage = ["owner", "admin"].includes(organizationRole);
  const ledgerContextKey = `${organizationId}:${organizationRole}:${organizationKind}`;
  const [ledgerOwnerKey, setLedgerOwnerKey] = useState(ledgerContextKey);
  const loadGeneration = useRef(0);
  const currentLedgerContextKey = useRef(ledgerContextKey);
  currentLedgerContextKey.current = ledgerContextKey;
  const ledgerIsCurrent = ledgerOwnerKey === ledgerContextKey;
  const visibleMembers = ledgerIsCurrent ? members : [];
  const visibleTeams = ledgerIsCurrent ? teams : [];
  const visibleInvitations = ledgerIsCurrent ? invitations : [];
  const visibleLoadState = ledgerIsCurrent ? loadState : "loading";
  const visibleLoadError = ledgerIsCurrent ? loadError : "";
  const openInvitations = visibleInvitations.filter((invitation) => invitationState(invitation) === "open").length;

  useLayoutEffect(() => {
    loadGeneration.current += 1;
    setLedgerOwnerKey(ledgerContextKey);
    setMembers([]);
    setTeams([]);
    setInvitations([]);
    setSharingSummary(null);
    setLoadState(organizationId ? "loading" : "ready");
    setLoadError("");
    setActionError("");
    setInviteOpen(false);
    setTeamOpen(false);
  }, [ledgerContextKey, organizationId]);

  const load = useCallback(async (showLoading = true) => {
    if (!organizationId || currentLedgerContextKey.current !== ledgerContextKey) return;
    const generation = ++loadGeneration.current;
    if (showLoading) {
      setLedgerOwnerKey(ledgerContextKey);
      setLoadState("loading");
    }
    setLoadError("");
    await requestPeopleLedger(
      { organizationId, canManage, isPersonal },
      api,
      () => loadGeneration.current === generation && currentLedgerContextKey.current === ledgerContextKey,
      (data) => {
        setLedgerOwnerKey(ledgerContextKey);
        setMembers(data.members);
        setTeams(data.teams);
        setInvitations(data.invitations);
        setLoadState("ready");
      },
      (caught) => {
        setLedgerOwnerKey(ledgerContextKey);
        setLoadError(caught instanceof Error ? caught.message : "The access ledger could not be loaded");
        setLoadState("error");
      },
    );
  }, [canManage, isPersonal, ledgerContextKey, organizationId]);

  useEffect(() => {
    void load();
    return () => { loadGeneration.current += 1; };
  }, [load]);

  const closeInvite = useCallback(() => setInviteOpen(false), []);
  const closeTeam = useCallback(() => setTeamOpen(false), []);

  async function deleteTeam(team: Team) {
    if (!window.confirm(`Delete ${team.name}? Its loops will become unassigned.`)) return;
    setActionError("");
    try {
      await api(`/api/app/teams/${team.id}`, { method: "DELETE" }, activeOrganization?.id);
      await load(false);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : `${team.name} could not be deleted`);
    }
  }

  async function revokeInvitation(invitation: Invitation) {
    const recipient = invitation.email || "this shareable link";
    if (!window.confirm(`Revoke the invitation for ${recipient}? The link will stop working immediately.`)) return;
    setActionError("");
    try {
      await api(`/api/app/invitations/${invitation.id}`, { method: "DELETE" }, activeOrganization?.id);
      await load(false);
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : "The invitation could not be revoked");
    }
  }

  const countsLine = isPersonal
    ? sharingSummary && sharingCountsLine({ loops: sharingSummary.loops, shared: sharingSummary.sharedLoops, rules: sharingSummary.rules })
    : visibleLoadState === "ready" && groupCountsLine({ members: visibleMembers.length, teams: visibleTeams.length, openInvitations: canManage ? openInvitations : null });

  return (
    <div className={`page admin-page people-page ${isPersonal ? "admin-page--personal" : "admin-page--group"}`}>
      <header className="admin-header">
        <h1>{isPersonal ? "Sharing" : "People"}</h1>
        <p>{isPersonal ? "Every loop stays private until you grant it to a group. Grants are per loop and reversible." : "See who belongs here, how access is shaped, and which invitations are still open."}</p>
        {countsLine && <p className="admin-header__facts" aria-live="polite">{countsLine}</p>}
      </header>

      {visibleLoadState === "loading" && <LoadingState>Reading membership, teams, and invitation state…</LoadingState>}
      {visibleLoadState === "error" && <ErrorState action={<Button variant="secondary" onClick={() => void load()}>Try again</Button>}>{visibleLoadError}</ErrorState>}
      {actionError && <div className="form-error admin-action-error" role="alert">{actionError}</div>}

      {visibleLoadState === "ready" && isPersonal && (
        <SharingStage organizationId={organizationId} canManage={canManage} onChanged={setSharingSummary} />
      )}

      {visibleLoadState === "ready" && !isPersonal && (
        <>
          <section className="panel admin-surface admin-team-section" aria-labelledby="team-heading">
            <header className="admin-surface__head">
              <div><h2 className="eyebrow" id="team-heading">Teams</h2><p>Teams organize people and loops inside this group; they do not widen the group boundary.</p></div>
              {canManage && <Button variant="secondary" onClick={() => setTeamOpen(true)}><Plus size={15} aria-hidden="true" /> New team</Button>}
            </header>
            {visibleTeams.length ? (
              <div className="admin-team-list">
                {visibleTeams.map((team) => (
                  <article key={team.id} style={{ "--team-color": team.color } as CSSProperties}>
                    <span className="admin-team-mark" aria-hidden="true" />
                    <div className="admin-team-identity"><strong>{team.name}</strong><small>{team.description || "No team description yet."}</small></div>
                    <span className="admin-team-count">{formatCount(team.memberCount, "person", "people")} · {formatCount(team.loopCount, "loop")}</span>
                    {canManage && visibleTeams.length > 1 ? <button className="icon-button admin-danger-action" aria-label={`Delete ${team.name}`} title={`Delete ${team.name}`} onClick={() => void deleteTeam(team)}><Trash2 size={15} aria-hidden="true" /></button> : <span />}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState eyebrow="No teams yet" title="Shape the first working set">Teams give members and loops a clear home inside the group.</EmptyState>
            )}
          </section>

          <section className="panel admin-surface admin-ledger-panel" aria-labelledby="member-heading">
            <header className="admin-surface__head">
              <div><h2 className="eyebrow" id="member-heading">Members</h2></div>
              {canManage ? <Button onClick={() => setInviteOpen(true)}><UserPlus size={15} aria-hidden="true" /> Invite someone</Button> : <span className="admin-readonly-label"><Shield size={13} aria-hidden="true" /> Read only</span>}
            </header>
            {visibleMembers.length ? (
              <div className="admin-member-list">
                {visibleMembers.map((member) => (
                  <article key={member.id}>
                    <span className="admin-avatar" aria-hidden="true">{memberInitials(member)}</span>
                    <div className="admin-member-identity"><strong>{member.displayName}</strong><small>{member.email}{member.title ? ` · ${member.title}` : ""}</small></div>
                    <div className="admin-member-teams" aria-label={`${member.displayName} teams`}>{member.teams.length ? member.teams.map((team) => <span key={team.id}>{team.name}</span>) : <small>No team</small>}</div>
                    <span className={`admin-role admin-role--${member.role}`}><Shield size={12} aria-hidden="true" />{member.role}</span>
                    <small className="admin-joined">Joined {formatRelative(member.joinedAt)}</small>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState eyebrow="No members returned" title="This group has no visible membership">Reload the group or ask an owner to confirm its access state.</EmptyState>
            )}
          </section>

          {canManage ? (
            <section className="panel admin-surface admin-ledger-panel admin-invite-panel" aria-labelledby="invitation-heading">
              <header className="admin-surface__head"><div><h2 className="eyebrow" id="invitation-heading">Invitations</h2></div><Button variant="secondary" onClick={() => setInviteOpen(true)}><Link2 size={15} aria-hidden="true" /> Create link</Button></header>
              {visibleInvitations.length ? (
                <div className="admin-invite-list">
                  {visibleInvitations.map((invitation) => {
                    const state = invitationState(invitation);
                    return (
                      <article key={invitation.id}>
                        <Link2 className="admin-invite-icon" size={15} aria-hidden="true" />
                        <div><strong>{invitation.email || "Shareable link"}</strong><small>{invitation.team_name || "Any team"} · {invitation.role} · expires {formatRelative(invitation.expires_at)}</small></div>
                        <span className={`admin-state-label admin-state-label--${state}`}><span aria-hidden="true" />{state}</span>
                        {state === "open" ? <button className="icon-button admin-danger-action" aria-label={`Revoke invitation for ${invitation.email || "shareable link"}`} title="Revoke invitation" onClick={() => void revokeInvitation(invitation)}><Trash2 size={14} aria-hidden="true" /></button> : <span />}
                      </article>
                    );
                  })}
                </div>
              ) : (
                <EmptyState eyebrow="No pending invitations" title="Every open door is accounted for">Invitation links appear here until accepted, revoked, or expired.</EmptyState>
              )}
            </section>
          ) : (
            <BoundaryCallout title="Membership is visible; administration is not." tone="shared">Your {activeOrganization?.role ?? "member"} role can read this group. An owner or admin creates teams and invitation links.</BoundaryCallout>
          )}

          <SharingStage organizationId={organizationId} canManage={false} mode="group" />
        </>
      )}

      <InviteModal open={inviteOpen} teams={visibleTeams} onClose={closeInvite} onSaved={() => void load(false)} />
      <TeamModal open={teamOpen} onClose={closeTeam} onSaved={() => { closeTeam(); void load(false); }} />
    </div>
  );
}

function InviteModal({ open, teams, onClose, onSaved }: { open: boolean; teams: Team[]; onClose: () => void; onSaved: () => void }) {
  const { activeOrganization } = useAuth();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [teamId, setTeamId] = useState("");
  const [url, setUrl] = useState("");
  const [delivered, setDelivered] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setEmail(""); setRole("member"); setTeamId(""); setUrl(""); setDelivered(null); setError(""); }
  }, [open]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ invitation: { url: string; email: string | null; delivered?: boolean } }>("/api/app/invitations", { method: "POST", body: JSON.stringify({ email: email || undefined, role, teamId: teamId || undefined, expiresInDays: 7 }) }, activeOrganization?.id);
      setUrl(result.invitation.url);
      setDelivered(result.invitation.delivered ? result.invitation.email : null);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create invitation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title={url ? (delivered ? "Invitation sent" : "Invitation ready") : "Invite someone"} onClose={onClose}>
      {url ? (
        <div className="admin-invite-result">
          <span><CheckCircle2 size={22} aria-hidden="true" /></span>
          <p>{delivered ? `We emailed the invitation to ${delivered}. It works once and expires in seven days. You can also share the link yourself.` : "This single-use link expires in seven days. Send it through a channel you trust."}</p>
          <code>{url}</code>
          <CopyButton value={url} label="Copy invitation" />
        </div>
      ) : (
        <form className="form-stack admin-modal-form" onSubmit={create}>
          <label><span>Email <small>optional</small></span><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="person@company.com" /></label>
          <div className="form-row"><label><span>Role</span><select value={role} onChange={(event) => setRole(event.target.value)}><option value="member">Member</option><option value="admin">Admin</option></select></label><label><span>Team</span><select value={teamId} onChange={(event) => setTeamId(event.target.value)}><option value="">No default team</option>{teams.map((team) => <option value={team.id} key={team.id}>{team.name}</option>)}</select></label></div>
          <div className="admin-action-note"><Copy size={15} aria-hidden="true" /><p>With an email address, LoopGroup sends the invitation and shows you the link too. Without one, you get a link to share through a channel you trust.</p></div>
          {error && <div className="form-error" role="alert">{error}</div>}
          <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy}>Create invitation</Button></div>
        </form>
      )}
    </Modal>
  );
}

function TeamModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { activeOrganization } = useAuth();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#FF3B00");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setName(""); setDescription(""); setColor("#FF3B00"); setError(""); }
  }, [open]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("/api/app/teams", { method: "POST", body: JSON.stringify({ name, description: description || undefined, color }) }, activeOrganization?.id);
      onSaved();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not create team");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title="Create a team" onClose={onClose}>
      <form className="form-stack admin-modal-form" onSubmit={create}>
        <label><span>Team name</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Growth" required minLength={2} /></label>
        <label><span>Description</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} rows={3} placeholder="What kind of loops live here?" /></label>
        <label><span>Signal color</span><div className="admin-color-field"><input aria-label="Team signal color" type="color" value={color} onChange={(event) => setColor(event.target.value)} /><code>{color}</code></div></label>
        {error && <div className="form-error" role="alert">{error}</div>}
        <div className="modal__actions"><Button type="button" variant="ghost" onClick={onClose}>Cancel</Button><Button type="submit" busy={busy}>Create team</Button></div>
      </form>
    </Modal>
  );
}
